"use strict";
// packages/shared-schemas/src/leadAssignedEmail.ts
//
// Settings + pure helpers for the LEAD-FACING "a broker has been assigned to
// you" email. This is the first VALUTTEN email that goes to the CONSUMER who
// enquired, so every word of it is tenant-owned: the tenant writes the subject
// and body, chooses the promised response timeframe, and decides whether it
// comes from the broker or a noreply address.
//
// Stored at `companies/{companyId}/lead_capture_config/lead_assigned_email`.
// That subcollection is NOT exposed in firestore.rules (it also holds the
// tenant's Resend secret pointer at .../resend), so it is reachable only
// through the admin SDK inside a callable. Settings are read and written via
// the leadAssignedEmailAction callable, never directly from the browser.
//
// Tri-state rollout gate, same shape and fail-closed semantics as
// leads/myCrmWriteBackGate.ts:
//   'off'      nothing happens (DEFAULT, and the value every unknown or
//              malformed stored value collapses to)
//   'observe'  the send is fully resolved and rendered, a would-send delivery
//              log row is written, and NO email leaves the building
//   'enforce'  the email is sent
//
// Everything in this module is pure: no Firestore, no network, no Node
// built-ins. It is imported by the client app (settings UI), the renderer and
// the sender, so it must stay dependency-free apart from zod.
Object.defineProperty(exports, "__esModule", { value: true });
exports.LEAD_ASSIGNED_EMAIL_DEFAULTS = exports.LEAD_ASSIGNED_DEFAULT_AFTER_HOURS_BODY = exports.LEAD_ASSIGNED_DEFAULT_AFTER_HOURS_SUBJECT = exports.LEAD_ASSIGNED_DEFAULT_BODY = exports.LEAD_ASSIGNED_DEFAULT_SUBJECT = exports.LeadAssignedEmailSettingsSchema = exports.ReplyToModeSchema = exports.LEAD_ASSIGNED_MERGE_FIELD_LABELS = exports.LEAD_ASSIGNED_MERGE_FIELDS = exports.ResponseTimeframeSchema = exports.RESPONSE_TIMEFRAME_UNITS = exports.LEAD_ASSIGNED_EMAIL_MODES = void 0;
exports.parseLeadAssignedEmailMode = parseLeadAssignedEmailMode;
exports.permitsLeadAssignedSend = permitsLeadAssignedSend;
exports.formatResponseTimeframe = formatResponseTimeframe;
exports.substituteLeadAssignedTokens = substituteLeadAssignedTokens;
exports.extractLeadAssignedTokens = extractLeadAssignedTokens;
exports.resolveLeadAssignedEmailSettings = resolveLeadAssignedEmailSettings;
exports.validateLeadAssignedEmailSettings = validateLeadAssignedEmailSettings;
exports.selectLeadAssignedCopy = selectLeadAssignedCopy;
exports.leadSourceAllowed = leadSourceAllowed;
const zod_1 = require("zod");
// ── Mode ────────────────────────────────────────────────────────────────────
exports.LEAD_ASSIGNED_EMAIL_MODES = ['off', 'observe', 'enforce'];
/** Missing, malformed and unknown values fail closed to 'off'. */
function parseLeadAssignedEmailMode(value) {
    return value === 'observe' || value === 'enforce' ? value : 'off';
}
/** Only the explicit enforce value authorises mail to a consumer. */
function permitsLeadAssignedSend(mode) {
    return mode === 'enforce';
}
// ── Response timeframe ──────────────────────────────────────────────────────
//
// DELIBERATELY SEPARATE from lead_routing_rules.defaultSlaMinutes. That value
// (default 15) is the INTERNAL escalation clock: if the broker has not made
// contact within it the lead reroutes. Promising a consumer "within 15
// minutes" because of an internal rerouting rule would be a commitment the
// tenant never made. The timeframe below is the tenant's own public promise.
exports.RESPONSE_TIMEFRAME_UNITS = [
    'minutes',
    'hours',
    'business_hours',
    'business_days',
];
exports.ResponseTimeframeSchema = zod_1.z.object({
    value: zod_1.z.number().int().min(1).max(365),
    unit: zod_1.z.enum(exports.RESPONSE_TIMEFRAME_UNITS),
    /**
     * Free text that REPLACES the generated phrase entirely, for promises the
     * value/unit pair cannot express ("by the end of the next business day").
     * Rendered as plain text, never as HTML.
     */
    label: zod_1.z.string().trim().max(120).optional(),
});
const UNIT_NOUNS = {
    minutes: ['minute', 'minutes'],
    hours: ['hour', 'hours'],
    business_hours: ['business hour', 'business hours'],
    business_days: ['business day', 'business days'],
};
/**
 * Render the timeframe as the phrase that lands in the email body, e.g.
 * "within 2 business days". A non-empty `label` wins verbatim so a tenant can
 * write a promise the structured pair cannot express.
 *
 * The phrase is written to read after "will be in touch ...", so it carries
 * its own leading "within".
 */
function formatResponseTimeframe(timeframe) {
    const label = (timeframe.label ?? '').trim();
    if (label)
        return label;
    const [one, many] = UNIT_NOUNS[timeframe.unit];
    return `within ${timeframe.value} ${timeframe.value === 1 ? one : many}`;
}
// ── Merge fields ────────────────────────────────────────────────────────────
exports.LEAD_ASSIGNED_MERGE_FIELDS = [
    'leadFirstName',
    'brokerFirstName',
    'brokerFullName',
    'brokerPhone',
    'brokerEmail',
    'companyName',
    'responseTimeframe',
    'enquiryType',
];
/** Short human labels for the merge-field chips in the settings editor. */
exports.LEAD_ASSIGNED_MERGE_FIELD_LABELS = {
    leadFirstName: "The enquirer's first name",
    brokerFirstName: "The assigned broker's first name",
    brokerFullName: "The assigned broker's full name",
    brokerPhone: "The assigned broker's phone number",
    brokerEmail: "The assigned broker's email address",
    companyName: 'Your business name',
    responseTimeframe: 'Your response promise, e.g. within 2 business hours',
    enquiryType: 'What the enquiry was about',
};
/** Matches `{{token}}`, tolerating inner spaces: `{{ brokerFullName }}`. */
const TOKEN_PATTERN = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
/**
 * Replace every `{{token}}` with its value.
 *
 * FAIL CLOSED: a token with no entry in `values`, or an entry that is empty
 * after trimming, is left in place AND reported in `unresolved`. The caller
 * must refuse to send rather than mail a consumer a literal `{{brokerPhone}}`.
 * An unknown token name (a typo) is treated the same way.
 *
 * Output is PLAIN TEXT. HTML escaping is the renderer's job, and is done by
 * React when the paragraphs are handed to the email component, so nothing a
 * tenant or a lead can write is ever interpreted as markup.
 */
function substituteLeadAssignedTokens(template, values) {
    const unresolved = new Set();
    const output = template.replace(TOKEN_PATTERN, (match, name) => {
        const value = (values[name] ?? '').trim();
        if (!value) {
            unresolved.add(name);
            return match;
        }
        return value;
    });
    return { output, unresolved: [...unresolved].sort() };
}
/** Distinct merge tokens a template uses, in first-seen order. */
function extractLeadAssignedTokens(template) {
    const seen = [];
    for (const match of template.matchAll(TOKEN_PATTERN)) {
        const name = match[1];
        if (!seen.includes(name))
            seen.push(name);
    }
    return seen;
}
// ── Settings schema ─────────────────────────────────────────────────────────
const SUBJECT_MAX = 200;
const BODY_MAX = 6000;
const AfterHoursSchema = zod_1.z.object({
    enabled: zod_1.z.boolean(),
    subject: zod_1.z.string().trim().max(SUBJECT_MAX).optional(),
    bodyTemplate: zod_1.z.string().trim().max(BODY_MAX).optional(),
});
exports.ReplyToModeSchema = zod_1.z.union([
    zod_1.z.literal('broker'),
    zod_1.z.literal('company'),
    zod_1.z.object({ custom: zod_1.z.string().trim().email().max(254) }),
]);
exports.LeadAssignedEmailSettingsSchema = zod_1.z.object({
    mode: zod_1.z.enum(exports.LEAD_ASSIGNED_EMAIL_MODES),
    responseTimeframe: exports.ResponseTimeframeSchema,
    subject: zod_1.z.string().trim().min(1).max(SUBJECT_MAX),
    bodyTemplate: zod_1.z.string().trim().min(1).max(BODY_MAX),
    afterHours: AfterHoursSchema,
    /**
     * 'broker'  from the assigned broker's localpart on the tenant's verified
     *           domain, reply-to their real mailbox
     * 'noreply' from noreply@ the verified domain under the company name
     */
    fromAddressMode: zod_1.z.union([zod_1.z.literal('broker'), zod_1.z.literal('noreply')]),
    replyTo: exports.ReplyToModeSchema,
    /** Manual reassignment is noisy for a consumer, so it is opt-in. */
    sendOnReassignment: zod_1.z.boolean(),
    includeBrokerPhone: zod_1.z.boolean(),
    /** Empty means every source. Otherwise only these lead sources are emailed. */
    sources: zod_1.z.array(zod_1.z.string().trim().min(1).max(80)).max(50).optional(),
});
// ── Default copy ────────────────────────────────────────────────────────────
//
// Warm, plain and factual. It states who has the enquiry, when they will make
// contact, and how to reply. It promises nothing about the loan itself. The
// founder owns this wording; a tenant can replace all of it.
exports.LEAD_ASSIGNED_DEFAULT_SUBJECT = '{{brokerFullName}} is looking after your enquiry';
exports.LEAD_ASSIGNED_DEFAULT_BODY = [
    'Hi {{leadFirstName}},',
    '',
    'Thanks for getting in touch with {{companyName}}.',
    '',
    'Your enquiry has been passed to {{brokerFullName}}, one of our brokers. {{brokerFirstName}} will be in touch {{responseTimeframe}} to talk through what you are after and answer any questions.',
    '',
    'If anything changes before then, or you would like to add anything to your enquiry, just reply to this email.',
    '',
    'Kind regards',
    '{{companyName}}',
].join('\n');
exports.LEAD_ASSIGNED_DEFAULT_AFTER_HOURS_SUBJECT = '{{brokerFullName}} will be in touch about your enquiry';
exports.LEAD_ASSIGNED_DEFAULT_AFTER_HOURS_BODY = [
    'Hi {{leadFirstName}},',
    '',
    'Thanks for getting in touch with {{companyName}}. Your enquiry has arrived outside our usual office hours, so there is nobody at the desk right now.',
    '',
    '{{brokerFullName}} will be looking after it and will contact you {{responseTimeframe}} once we are open again.',
    '',
    'If it is urgent, reply to this email and let us know.',
    '',
    'Kind regards',
    '{{companyName}}',
].join('\n');
exports.LEAD_ASSIGNED_EMAIL_DEFAULTS = {
    mode: 'off',
    responseTimeframe: { value: 2, unit: 'business_hours' },
    subject: exports.LEAD_ASSIGNED_DEFAULT_SUBJECT,
    bodyTemplate: exports.LEAD_ASSIGNED_DEFAULT_BODY,
    afterHours: {
        enabled: true,
        subject: exports.LEAD_ASSIGNED_DEFAULT_AFTER_HOURS_SUBJECT,
        bodyTemplate: exports.LEAD_ASSIGNED_DEFAULT_AFTER_HOURS_BODY,
    },
    fromAddressMode: 'broker',
    replyTo: 'broker',
    sendOnReassignment: false,
    includeBrokerPhone: false,
    sources: [],
};
/**
 * Merge a stored document over the defaults.
 *
 * A stored doc that fails validation (hand-edited, partially written, or from
 * an older shape) yields the DEFAULTS, whose mode is 'off'. A tenant therefore
 * cannot end up mailing consumers off a config nobody could parse.
 */
function resolveLeadAssignedEmailSettings(stored) {
    if (!stored || typeof stored !== 'object')
        return exports.LEAD_ASSIGNED_EMAIL_DEFAULTS;
    const raw = stored;
    const merged = {
        ...exports.LEAD_ASSIGNED_EMAIL_DEFAULTS,
        ...raw,
        // Nested objects merge field by field, so a doc that stores only
        // `afterHours.enabled` keeps the default copy rather than losing it.
        responseTimeframe: {
            ...exports.LEAD_ASSIGNED_EMAIL_DEFAULTS.responseTimeframe,
            ...(raw.responseTimeframe ?? {}),
        },
        afterHours: {
            ...exports.LEAD_ASSIGNED_EMAIL_DEFAULTS.afterHours,
            ...(raw.afterHours ?? {}),
        },
        mode: parseLeadAssignedEmailMode(raw.mode),
    };
    const parsed = exports.LeadAssignedEmailSettingsSchema.safeParse(merged);
    return parsed.success ? parsed.data : exports.LEAD_ASSIGNED_EMAIL_DEFAULTS;
}
/**
 * Config-level validation that cannot be expressed in the zod shape.
 *
 * The important one: a body that uses `{{brokerPhone}}` while
 * `includeBrokerPhone` is false would fail closed at SEND time and silently
 * stop every email for that tenant. Catching it at the edit surface turns a
 * silent outage into an inline error the author can see and fix.
 *
 * Returns human-readable problems; an empty array means the settings are
 * internally consistent.
 */
function validateLeadAssignedEmailSettings(settings) {
    const problems = [];
    const known = new Set(exports.LEAD_ASSIGNED_MERGE_FIELDS);
    const checkTemplate = (label, template) => {
        for (const token of extractLeadAssignedTokens(template)) {
            if (!known.has(token)) {
                problems.push(`${label} uses an unknown merge field {{${token}}}.`);
            }
            else if (token === 'brokerPhone' && !settings.includeBrokerPhone) {
                problems.push(`${label} uses {{brokerPhone}}, but "include the broker's phone number" is off. Turn it on or remove the merge field, otherwise no email will be sent.`);
            }
        }
    };
    checkTemplate('The subject', settings.subject);
    checkTemplate('The message', settings.bodyTemplate);
    if (settings.afterHours.enabled) {
        if (settings.afterHours.subject) {
            checkTemplate('The after-hours subject', settings.afterHours.subject);
        }
        if (settings.afterHours.bodyTemplate) {
            checkTemplate('The after-hours message', settings.afterHours.bodyTemplate);
        }
    }
    return problems;
}
/** The subject/body pair actually used for a send, after-hours aware. */
function selectLeadAssignedCopy(settings, afterHours) {
    if (afterHours && settings.afterHours.enabled) {
        return {
            subject: (settings.afterHours.subject ?? '').trim() || settings.subject,
            bodyTemplate: (settings.afterHours.bodyTemplate ?? '').trim() || settings.bodyTemplate,
        };
    }
    return { subject: settings.subject, bodyTemplate: settings.bodyTemplate };
}
/** True when this lead's source passes the optional allow-list. */
function leadSourceAllowed(settings, source) {
    const allowed = settings.sources ?? [];
    if (allowed.length === 0)
        return true;
    const normalised = (source ?? '').trim().toLowerCase();
    return allowed.some((s) => s.trim().toLowerCase() === normalised);
}
